import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ConfirmDialog from './ConfirmDialog';
import { useFullscreen } from '../contexts/FullscreenContext';
import { useChat } from '../contexts/ChatContext';
import chatService from '../services/chatService';
import { chatErrorMessage } from '../services/chatErrorMessage';
import ChatMessage from './chat/ChatMessage';
import ChatProgress from './chat/ChatProgress';
import uiActionHandler from '../services/uiActionHandler';
import { useEntitlements } from '../books/entitlements';
import {
  processQuickCommand,
  getQuickCommandHelp,
} from '../utils/quickCommands';
import BathroomConstraintsModal from './BathroomConstraintsPanel';
import { inferRoomType } from '../utils/roomTypeUtils';
import './LogoPopup.css';

/**
 * Lay actions out in rows. Consecutive actions sharing the same `group` string
 * stay together and never share a row with a neighbouring group, so a caller
 * can keep e.g. the two checkpoint buttons on their own line no matter how
 * many items precede them. Ungrouped actions behave as before (a single run
 * chunked two-per-row). A run's first action may set `perRow` to widen its
 * rows (the three exports ride one row).
 */
// Running an action normally dismisses the menu — you asked for a thing, the
// thing happens, the menu is out of the way. An action that changes what the
// MENU ITSELF shows is the exception: it sets `keepOpen`, so revealing the
// admin buttons leaves them on screen instead of making you reopen the menu
// to see what changed.
const runAction = (action, close) => {
  if (action.disabled) return;
  action.onClick();
  if (!action.keepOpen) close();
};

const chunkActionRows = (actions) => {
  const runs = [];
  actions.forEach((action) => {
    const group = action.group ?? null;
    const last = runs[runs.length - 1];
    if (last && last.group === group) last.actions.push(action);
    else runs.push({ group, actions: [action] });
  });

  return runs.flatMap((run) => {
    const perRow = run.actions[0]?.perRow || 2;
    const rows = [];
    for (let i = 0; i < run.actions.length; i += perRow) {
      rows.push(run.actions.slice(i, i + perRow));
    }
    return rows;
  });
};

const LogoPopup = ({
  onLogout,
  customActions = [],
  onExportPDF,
  onExportIFC,
  onExportDXF,
  exportLoading = false,
  roomData = null,
  structureData = null,
  disabledConstraints = [],
  onDisabledConstraintsChange,
  // Admin tooling visible (isAdmin && the "A" toggle) — gates the
  // Bathroom Constraints reference, which is a dev aid, not a user tool.
  showAdminTools = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Note: isClosing state and animation removed - was causing UI freezes
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showConstraints, setShowConstraints] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const { mayUseAssistant } = useEntitlements();

  // Get chat state from context
  const {
    messages,
    isLoading,
    currentTaskId,
    progress,
    inputValue,
    isChatActive,
    conversationContext,
    setMessages,
    setIsLoading,
    setCurrentTaskId,
    setProgress,
    setInputValue,
    setIsChatActive,
    addMessage,
    clearMessages,
    updateContext,
    trackNavigation,
    browserLoop,
    sendBrowserTurn,
    stopTurn,
    turnRunning,
  } = useChat();

  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const navigate = useNavigate();
  const location = useLocation();
  const popupRef = useRef(null);
  const logoRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);

  const isProjectPage = location.pathname.startsWith('/project');

  // Set up UI action handlers
  useEffect(() => {
    // Set up navigation handler
    uiActionHandler.setNavigationCallback((path, options) => {
      if (typeof path === 'number') {
        // Handle back/forward navigation
        navigate(path);
      } else {
        navigate(path);
        // Track navigation for context
        trackNavigation(path);
      }
      // Close the popup after navigation
      handleClose();
    });

    // Set up scene action handler
    uiActionHandler.setSceneCallback((actionType, subAction, data) => {
      console.log('LogoPopup scene callback:', { actionType, subAction, data });
      // Dispatch custom event for scene components to handle
      const event = new CustomEvent('sceneAction', {
        detail: { actionType, subAction, data },
      });
      window.dispatchEvent(event);
    });

    // Set up action event listeners
    const handleTriggerExport = (e) => {
      const format = e.detail?.format;
      if (format === 'pdf' && onExportPDF) {
        onExportPDF();
        handleClose();
      } else if (format === 'ifc' && onExportIFC) {
        onExportIFC();
        handleClose();
      } else if (format === 'dxf' && onExportDXF) {
        onExportDXF();
        handleClose();
      }
    };

    const handleToggleFullscreen = () => {
      toggleFullscreen();
      handleClose();
    };

    const handleClearChatEvent = () => {
      clearMessages();
    };

    const handleTriggerLogout = () => {
      handleLogout();
    };

    const handleTriggerCustomAction = (e) => {
      const { label } = e.detail;
      const action = customActions.find((a) => a.label === label);
      if (action) runAction(action, handleClose);
    };

    window.addEventListener('triggerExport', handleTriggerExport);
    window.addEventListener('toggleFullscreen', handleToggleFullscreen);
    window.addEventListener('clearChat', handleClearChatEvent);
    window.addEventListener('triggerLogout', handleTriggerLogout);
    window.addEventListener('triggerCustomAction', handleTriggerCustomAction);

    return () => {
      // Clean up handlers on unmount
      uiActionHandler.setNavigationCallback(null);
      uiActionHandler.setSceneCallback(null);
      window.removeEventListener('triggerExport', handleTriggerExport);
      window.removeEventListener('toggleFullscreen', handleToggleFullscreen);
      window.removeEventListener('clearChat', handleClearChatEvent);
      window.removeEventListener('triggerLogout', handleTriggerLogout);
      window.removeEventListener(
        'triggerCustomAction',
        handleTriggerCustomAction
      );
    };
  }, [
    navigate,
    trackNavigation,
    onExportPDF,
    onExportIFC,
    onExportDXF,
    toggleFullscreen,
    clearMessages,
    customActions,
  ]);

  // Handle Enter key to open chat
  useEffect(() => {
    if (!mayUseAssistant) return undefined;
    const handleKeyPress = (e) => {
      // Only open if Enter is pressed and chat is not already open
      // Don't trigger if user is typing in an input field or textarea
      if (e.key === 'Enter' && !isOpen) {
        const activeElement = document.activeElement;
        // Enter must keep its native meaning on anything focusable: typing
        // in a field, activating a focused button/link/select (e.g. the
        // "Delete" in a confirm dialog, "Log In"), or anything inside an
        // open dialog. Only a bare page (body / non-interactive focus)
        // opens the chat.
        const tag = activeElement?.tagName;
        const isInteractive =
          activeElement &&
          (tag === 'INPUT' ||
            tag === 'TEXTAREA' ||
            tag === 'SELECT' ||
            tag === 'BUTTON' ||
            tag === 'A' ||
            activeElement.isContentEditable ||
            activeElement.getAttribute('role') === 'button' ||
            activeElement.closest('[role="dialog"]'));

        if (!isInteractive) {
          e.preventDefault();
          openChat();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, mayUseAssistant]);

  // A failed turn shows in the conversation, not only in the console.
  const reportChatError = (error) => {
    addMessage({
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: 'assistant',
      content: chatErrorMessage(error),
      timestamp: new Date(),
    });
  };

  const openChat = () => {
    if (!isOpen) {
      setIsOpen(true);
      // Focus the text area after opening
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 50);
    }
  };

  const handleLogoClick = () => {
    if (isOpen) {
      handleClose();
    } else {
      openChat();
    }
  };

  const handleClose = () => {
    // Close immediately - animation was causing UI freezes
    if (!isOpen) return;
    setIsOpen(false);
  };

  const handleBackToProjects = () => {
    navigate('/');
    handleClose();
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    onLogout();
    handleClose();
    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleFullscreen = () => {
    toggleFullscreen();
    handleClose();
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickCommand = async (command) => {
    let responseMessage = '';

    switch (command.type) {
      case 'navigate':
        // Use custom message if provided, otherwise generate one
        responseMessage =
          command.message ||
          `Navigating ${command.path === -1 ? 'back' : command.path === 1 ? 'forward' : 'to ' + command.path}...`;
        navigate(command.path);
        if (typeof command.path === 'string') {
          trackNavigation(command.path);
        }
        // Close popup after navigation
        setTimeout(() => handleClose(), 100);
        break;

      case 'navigateProject':
        responseMessage = `Searching for project "${command.projectName}"...`;
        // This would need to be handled by AI since we need to search
        // Fall through to AI processing
        return handleAIFallback(command.originalInput);

      case 'navigateRoom':
        responseMessage = `Searching for ${command.roomName}...`;
        // This would need to be handled by AI since we need to search
        return handleAIFallback(command.originalInput);

      case 'action':
        switch (command.action) {
          case 'logout':
            responseMessage = 'Logging out...';
            handleLogout();
            break;
          case 'fullscreen':
            responseMessage = 'Toggling fullscreen...';
            toggleFullscreen();
            handleClose();
            break;
          case 'clearChat':
            responseMessage = 'Chat cleared.';
            clearMessages();
            return; // Don't add response message since we're clearing
          case 'exportPDF':
            if (onExportPDF) {
              responseMessage = 'Exporting as PDF...';
              onExportPDF();
              handleClose();
            } else {
              responseMessage = 'PDF export is not available in this context.';
            }
            break;
          case 'exportIFC':
            if (onExportIFC) {
              responseMessage = 'Exporting as IFC...';
              onExportIFC();
              handleClose();
            } else {
              responseMessage = 'IFC export is not available in this context.';
            }
            break;
          case 'exportDXF':
            if (onExportDXF) {
              responseMessage = 'Exporting as DXF...';
              onExportDXF();
              handleClose();
            } else {
              responseMessage = 'DXF export is not available in this context.';
            }
            break;
          case 'openCheckpoints':
            responseMessage = 'Opening checkpoints...';
            window.dispatchEvent(new CustomEvent('openCheckpointsModal'));
            // Don't close immediately - let the modal open first
            setTimeout(() => handleClose(), 100);
            break;
          case 'noStructureForCheckpoints':
            responseMessage = command.message;
            break;
          case 'openRenderings':
            responseMessage = 'Opening AI renderings...';
            window.dispatchEvent(new CustomEvent('openRenderingsModal'));
            // Don't close immediately - let the modal open first
            setTimeout(() => handleClose(), 100);
            break;
          case 'noStructureForRenderings':
            responseMessage = command.message;
            break;
          case 'switchViewMode':
            responseMessage =
              command.message || `Switching to ${command.viewMode} view...`;
            // Dispatch event to switch view mode
            window.dispatchEvent(
              new CustomEvent('switchViewMode', {
                detail: { viewMode: command.viewMode },
              })
            );
            handleClose();
            break;
        }
        break;

      case 'help':
        responseMessage = getQuickCommandHelp();
        break;

      case 'showContext':
        // Format the context for display
        const ctx = conversationContext;
        const contextInfo = {
          location: ctx.location || {},
          navigation: {
            lastProject: ctx.navigation?.lastProject || null,
            lastStructure: ctx.navigation?.lastStructure || null,
            historyCount: ctx.navigation?.history?.length || 0,
          },
          cache: {
            projects: ctx.cache?.projects?.length || 0,
            structures: ctx.cache?.structures?.length || 0,
            rooms: ctx.cache?.rooms?.length || 0,
            checkpoints: ctx.cache?.checkpoints?.length || 0,
          },
          ui: ctx.ui || {},
          user: ctx.user || {},
        };

        // Add cache details if there's data
        let cacheDetails = '';
        if (ctx.cache?.projects?.length > 0) {
          cacheDetails += '\n\n**Cached Projects:**\n';
          ctx.cache.projects.slice(0, 3).forEach((p) => {
            cacheDetails += `- ${p.name} (${p.id})\n`;
          });
          if (ctx.cache.projects.length > 3) {
            cacheDetails += `... and ${ctx.cache.projects.length - 3} more\n`;
          }
        }
        if (ctx.cache?.structures?.length > 0) {
          cacheDetails += '\n**Cached Structures:**\n';
          ctx.cache.structures.slice(0, 3).forEach((s) => {
            cacheDetails += `- ${s.name} (${s.id})\n`;
          });
          if (ctx.cache.structures.length > 3) {
            cacheDetails += `... and ${ctx.cache.structures.length - 3} more\n`;
          }
        }
        if (ctx.cache?.rooms?.length > 0) {
          cacheDetails += '\n**Cached Rooms:**\n';
          ctx.cache.rooms.slice(0, 5).forEach((r) => {
            cacheDetails += `- ${r.name || 'Unnamed Room'} (${r.type || 'unknown'})\n`;
          });
          if (ctx.cache.rooms.length > 5) {
            cacheDetails += `... and ${ctx.cache.rooms.length - 5} more\n`;
          }
        }

        responseMessage =
          '**Current Context:**\n```json\n' +
          JSON.stringify(contextInfo, null, 2) +
          '\n```' +
          cacheDetails;
        break;

      default:
        responseMessage = 'Command recognized but not implemented.';
    }

    // Add system response to chat
    if (responseMessage) {
      addMessage({
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: responseMessage,
        timestamp: new Date(),
        isQuickCommand: true,
      });
    }
  };

  const handleAIFallback = async (userMessage) => {
    // Process with AI when quick command needs more context
    const newUserMessage = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    addMessage(newUserMessage);

    setIsLoading(true);
    setProgress(null);

    // Continue with normal AI processing...
    try {
      // Gather available actions for AI context
      const availableActions = [
        ...customActions.map((a) => ({
          id: a.label,
          label: a.label,
          enabled: !a.disabled,
        })),
        onExportPDF && {
          id: 'export_pdf',
          label: 'Export PDF',
          enabled: !exportLoading,
        },
        onExportIFC && {
          id: 'export_ifc',
          label: 'Export IFC',
          enabled: !exportLoading,
        },
        onExportDXF && {
          id: 'export_dxf',
          label: 'Export DXF',
          enabled: !exportLoading,
        },
      ].filter(Boolean);

      // Send message to chat service with context (prefer persisted over URL)
      // Use the clean context directly - no duplicate extraction
      const messageContext = {
        ...conversationContext,
        ui: {
          ...conversationContext.ui,
          availableActions,
        },
      };

      console.log('Sending message with context:', {
        location: messageContext.location,
        structuresCount: messageContext.cache?.structures?.length,
        structures: messageContext.cache?.structures,
      });

      const response = await chatService.sendMessage(
        userMessage,
        messageContext
      );
      const { taskId } = response;
      setCurrentTaskId(taskId);

      // Subscribe to progress updates
      chatService.subscribeToProgress(
        taskId,
        (progressData) => {
          setProgress(progressData);
          // Scroll when progress updates
          setTimeout(() => {
            scrollToBottom();
          }, 50);
        },
        (result, uiActions, activity) => {
          // Add assistant response with activity feed if available
          const assistantMessage = {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            role: 'assistant',
            content: result.message,
            activity: activity && activity.length > 0 ? activity : undefined,
            timestamp: new Date(),
          };
          addMessage(assistantMessage);
          setIsLoading(false);
          setProgress(null);
          setCurrentTaskId(null);

          // Focus the input for continued typing
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);

          // Handle UI actions if needed
          if (uiActions && uiActions.length > 0) {
            console.log('UI Actions received:', uiActions);
            // Execute UI actions
            uiActions.forEach((action) => {
              uiActionHandler.executeAction(action);
            });
          }
        },
        (error) => {
          console.error('Chat error:', error);
          reportChatError(error);
          setIsLoading(false);
          setProgress(null);
          setCurrentTaskId(null);

          // Focus the input even on error
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);
        }
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      reportChatError(error);
      setIsLoading(false);
      setProgress(null);
    }
  };

  const startSpeechRecognition = () => {
    if (
      !('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)
    ) {
      alert(
        'Speech recognition is not supported in your browser. Please use Chrome or Edge.'
      );
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setInputValue((prev) => prev + finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    // Stop listening if active
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }

    const userMessage = inputValue.trim();
    setInputValue('');

    // Reset textarea height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Activate chat mode
    if (!isChatActive) {
      setIsChatActive(true);
    }

    // The browser-run turn with the app's own actions (admins, for now): the
    // model does what the quick commands do, with show.
    if (browserLoop.on) {
      await sendBrowserTurn(userMessage);
      return;
    }

    // Check for quick command with clean context
    const commandContext = {
      location: conversationContext.location || {},
      cache: conversationContext.cache || {},
      navigation: conversationContext.navigation || {},
    };

    const quickCommand = processQuickCommand(userMessage, commandContext);
    if (quickCommand) {
      // Add user message to chat
      addMessage({
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'user',
        content: userMessage,
        timestamp: new Date(),
      });

      // Handle quick command
      if (quickCommand.needsAI) {
        // This pattern needs AI to search/process
        await handleAIFallback(userMessage);
      } else {
        // Can be handled locally
        await handleQuickCommand(quickCommand);
      }
      return;
    }

    // Add user message to the chat
    const newUserMessage = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    addMessage(newUserMessage);

    setIsLoading(true);
    setProgress(null);

    // Scroll to bottom immediately after adding user message
    setTimeout(() => {
      scrollToBottom();
    }, 50);

    try {
      // Gather available actions for AI context
      const availableActions = [
        ...customActions.map((a) => ({
          id: a.label,
          label: a.label,
          enabled: !a.disabled,
        })),
        onExportPDF && {
          id: 'export_pdf',
          label: 'Export PDF',
          enabled: !exportLoading,
        },
        onExportIFC && {
          id: 'export_ifc',
          label: 'Export IFC',
          enabled: !exportLoading,
        },
        onExportDXF && {
          id: 'export_dxf',
          label: 'Export DXF',
          enabled: !exportLoading,
        },
      ].filter(Boolean);

      // Send message to chat service with context (prefer persisted over URL)
      // Use the clean context directly - no duplicate extraction
      const messageContext = {
        ...conversationContext,
        ui: {
          ...conversationContext.ui,
          availableActions,
        },
      };

      console.log('Sending message with context:', {
        location: messageContext.location,
        structuresCount: messageContext.cache?.structures?.length,
        structures: messageContext.cache?.structures,
      });

      const response = await chatService.sendMessage(
        userMessage,
        messageContext
      );
      const { taskId } = response;
      setCurrentTaskId(taskId);

      // Subscribe to progress updates
      chatService.subscribeToProgress(
        taskId,
        (progressData) => {
          setProgress(progressData);
          // Scroll when progress updates
          setTimeout(() => {
            scrollToBottom();
          }, 50);
        },
        (result, uiActions, activity) => {
          // Add assistant response with activity feed if available
          const assistantMessage = {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            role: 'assistant',
            content: result.message,
            activity: activity && activity.length > 0 ? activity : undefined,
            timestamp: new Date(),
          };
          addMessage(assistantMessage);
          setIsLoading(false);
          setProgress(null);
          setCurrentTaskId(null);

          // Focus the input for continued typing
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);

          // Handle UI actions if needed
          if (uiActions && uiActions.length > 0) {
            console.log('UI Actions received:', uiActions);
            // Execute UI actions
            uiActions.forEach((action) => {
              uiActionHandler.executeAction(action);
            });
          }
        },
        (error) => {
          console.error('Chat error:', error);
          reportChatError(error);
          setIsLoading(false);
          setProgress(null);
          setCurrentTaskId(null);

          // Focus the input even on error
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);
        }
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      reportChatError(error);
      setIsLoading(false);
      setProgress(null);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Add a small delay to ensure DOM is rendered. `progress` too: a turn the
    // browser runs reports its steps through it as they happen.
    if (isOpen && isChatActive) {
      setTimeout(() => {
        scrollToBottom();
      }, 50);
    }
  }, [messages, progress, isOpen, isChatActive]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        logoRef.current &&
        !logoRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    // Use mousedown with capture phase to catch events before they reach the canvas
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen]);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current && isListening) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Auto-resize textarea when value changes programmatically (e.g., from speech recognition)
  // or when the popup opens
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      if (inputValue) {
        textareaRef.current.style.height =
          Math.min(textareaRef.current.scrollHeight, 120) + 'px';
      }
      // Auto-focus textarea when popup opens or chat mode changes
      if (isOpen) {
        textareaRef.current.focus();
      }
    }
  }, [inputValue, isOpen, isChatActive]);

  return (
    <>
      <img
        ref={logoRef}
        src="/AppLogo.png"
        alt="Housfully"
        className="header-logo clickable-logo"
        onClick={handleLogoClick}
        style={{ position: 'relative', zIndex: 1003 }} // Keep logo above popup content
      />

      {isOpen && (
        <>
          {/* Invisible backdrop to catch clicks */}
          {/* Backdrop - click handling is done via document listener */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1001,
              background: 'transparent',
            }}
          />
          <div className="logo-popup-overlay">
            <div
              ref={popupRef}
              className="logo-popup-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="logo-popup-body">
                {/* Housfully text next to logo */}
                <div
                  style={{
                    position: 'absolute',
                    top: '16px',
                    left: '80px' /* Position to the right of logo with more gap (16px padding + 48px logo + 16px gap) */,
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#54524F',
                    display: 'flex',
                    alignItems: 'center',
                    height: '48px',
                  }}
                >
                  Housfully
                </div>
                {/* The assistant: contractors and admins only (mayUseAssistant). */}
                {mayUseAssistant &&
                  (!isChatActive ? (
                    // Initial input view
                    <div
                      className="input-container"
                      style={{ marginTop: '0', marginLeft: '0' }}
                    >
                      <textarea
                        ref={textareaRef}
                        className="logo-popup-input"
                        placeholder="Type your request here."
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      />
                      {/* Microphone icon - top left */}
                      <svg
                        className={`microphone-icon ${isListening ? 'recording' : ''}`}
                        onClick={startSpeechRecognition}
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '12px',
                          cursor: 'pointer',
                          opacity: isListening ? 1 : 0.6,
                          color: isListening ? '#FF0000' : '#54524F',
                        }}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        title={
                          isListening ? 'Stop recording' : 'Start voice input'
                        }
                      >
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                      <div className="quick-command-hint">
                        Try: "help", "home", "export pdf", or type naturally
                      </div>
                      {/* Send icon - bottom right */}
                      <svg
                        className="send-icon"
                        onClick={handleSendMessage}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          bottom: '12px',
                          cursor:
                            inputValue.trim() && !isLoading
                              ? 'pointer'
                              : 'not-allowed',
                          opacity: inputValue.trim() && !isLoading ? 1 : 0.3,
                          color: '#007AFF',
                        }}
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        title="Send message"
                      >
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    </div>
                  ) : (
                    // Chat view
                    <div className="logo-popup-chat-container">
                      <div className="logo-popup-messages">
                        {messages.map((message) => (
                          <ChatMessage key={message.id} message={message} />
                        ))}
                        {progress && <ChatProgress progress={progress} />}
                        {turnRunning && (
                          <button
                            type="button"
                            className="chat-stop-button"
                            onClick={stopTurn}
                          >
                            Stop
                          </button>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                      <div className="logo-popup-input-wrapper">
                        <textarea
                          ref={textareaRef}
                          className="logo-popup-chat-input"
                          placeholder="Type your message..."
                          value={inputValue}
                          onChange={handleInputChange}
                          onKeyDown={handleInputKeyDown}
                          disabled={isLoading}
                          rows="1"
                          style={{ paddingLeft: '40px', paddingRight: '40px' }}
                        />
                        {/* Microphone icon for chat mode */}
                        <svg
                          className={`microphone-icon ${isListening ? 'recording' : ''}`}
                          onClick={startSpeechRecognition}
                          style={{
                            position: 'absolute',
                            left: '12px',
                            top: '10px',
                            cursor: 'pointer',
                            opacity: isListening ? 1 : 0.6,
                            color: isListening ? '#FF0000' : '#54524F',
                          }}
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          title={
                            isListening ? 'Stop recording' : 'Start voice input'
                          }
                        >
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                          <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                        <svg
                          className="send-icon"
                          onClick={handleSendMessage}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            bottom: '16px',
                            cursor:
                              inputValue.trim() && !isLoading
                                ? 'pointer'
                                : 'not-allowed',
                            opacity: inputValue.trim() && !isLoading ? 1 : 0.3,
                            color: '#007AFF',
                          }}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="22" y1="2" x2="11" y2="13" />
                          <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                      </div>
                    </div>
                  ))}

                {/* Chat action buttons - shown when chat is active */}
                {mayUseAssistant && isChatActive && (
                  <div className="action-buttons-container">
                    <div className="action-buttons">
                      <button className="action-button" onClick={clearMessages}>
                        Delete Chat
                      </button>
                      {/* Admins, while it is tried: the app's own actions,
                          run by the browser, or the server-run chat. */}
                      {browserLoop.available && (
                        <button
                          className="action-button"
                          onClick={() => browserLoop.setOn(!browserLoop.on)}
                          title="Let the assistant use the app's own actions, show and look"
                        >
                          App actions: {browserLoop.on ? 'On' : 'Off'}
                        </button>
                      )}
                      <button
                        className="action-button"
                        onClick={() => {
                          handleClose();
                          // Keep messages and chat state for when user reopens
                        }}
                      >
                        Minimize Chat
                      </button>
                    </div>
                  </div>
                )}

                {!isChatActive && (
                  <div className="action-buttons-container">
                    {/* Render non-admin actions first, in rows of 2 */}
                    {(() => {
                      const regularActions = customActions.filter(
                        (action) => !action.isAdmin
                      );
                      const adminActions = customActions.filter(
                        (action) => action.isAdmin
                      );

                      return (
                        <>
                          {/* Regular actions */}
                          {regularActions.length > 0 && (
                            <>
                              {chunkActionRows(regularActions).map(
                                (rowActions, rowIndex) => (
                                  <div
                                    key={`regular-${rowIndex}`}
                                    className="action-buttons"
                                  >
                                    {rowActions.map((action) => (
                                      <button
                                        key={action.label}
                                        className="action-button"
                                        onClick={() =>
                                          runAction(action, handleClose)
                                        }
                                        disabled={action.disabled}
                                        title={action.title}
                                      >
                                        {action.label}
                                      </button>
                                    ))}
                                  </div>
                                )
                              )}
                            </>
                          )}

                          {/* Back to Projects button for project page without custom actions */}
                          {!regularActions.length &&
                            !adminActions.length &&
                            isProjectPage && (
                              <div className="action-buttons">
                                <button
                                  className="action-button"
                                  onClick={handleBackToProjects}
                                >
                                  Back to Projects
                                </button>
                              </div>
                            )}

                          {/* Export buttons - render after regular actions */}
                          {(onExportPDF || onExportIFC || onExportDXF) && (
                            <div
                              className="action-buttons"
                              style={{ display: 'flex', gap: '8px' }}
                            >
                              {onExportPDF && (
                                <button
                                  className="action-button"
                                  style={{ flex: '1 1 auto', minWidth: '80px' }}
                                  onClick={() => {
                                    onExportPDF();
                                    handleClose();
                                  }}
                                  disabled={exportLoading}
                                >
                                  {exportLoading
                                    ? 'Exporting...'
                                    : 'Export PDF'}
                                </button>
                              )}
                              {onExportIFC && (
                                <button
                                  className="action-button"
                                  style={{ flex: '1 1 auto', minWidth: '80px' }}
                                  onClick={() => {
                                    onExportIFC();
                                    handleClose();
                                  }}
                                  disabled={exportLoading}
                                >
                                  Export IFC
                                </button>
                              )}
                              {onExportDXF && (
                                <button
                                  className="action-button"
                                  style={{ flex: '1 1 auto', minWidth: '80px' }}
                                  onClick={() => {
                                    onExportDXF();
                                    handleClose();
                                  }}
                                  disabled={exportLoading}
                                >
                                  Export DXF
                                </button>
                              )}
                            </div>
                          )}

                          {/* Admin actions - render after export buttons */}
                          {adminActions.length > 0 && (
                            <>
                              {chunkActionRows(adminActions).map(
                                (rowActions, rowIndex) => (
                                  <div
                                    key={`admin-${rowIndex}`}
                                    className="action-buttons"
                                  >
                                    {rowActions.map((action) => (
                                      <button
                                        key={action.label}
                                        className="action-button admin-action-button"
                                        onClick={() =>
                                          runAction(action, handleClose)
                                        }
                                        disabled={action.disabled}
                                        title={action.title}
                                      >
                                        {action.label}
                                      </button>
                                    ))}
                                  </div>
                                )
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}

                    {/* Bathroom Constraints Reference - admin tool, bathrooms only */}
                    {showAdminTools &&
                      inferRoomType(roomData?.name) === 'Bathroom' && (
                        <div className="action-buttons">
                          <button
                            className="action-button admin-action-button"
                            onClick={() => {
                              setShowConstraints(true);
                              handleClose();
                            }}
                          >
                            Bathroom Constraints
                          </button>
                        </div>
                      )}

                    {/* Logout button - always last */}
                    <div className="action-buttons logout-row">
                      <button
                        className="action-button logout-action"
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
        title="Logout Confirmation"
        message="Are you sure you want to logout from your account?"
        confirmText="Logout"
        cancelText="Cancel"
        confirmButtonStyle="danger"
      />

      <BathroomConstraintsModal
        isOpen={showConstraints}
        onClose={() => setShowConstraints(false)}
        roomData={roomData}
        structureData={structureData}
        disabledConstraints={disabledConstraints}
        onDisabledConstraintsChange={onDisabledConstraintsChange}
      />
    </>
  );
};

export default LogoPopup;
